import { db } from '../config/firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';

/**
 * Member Service
 * Manages user-level members that can be reused across trips
 */

/**
 * Get all members for a user
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} Array of member objects
 */
export const getUserMembers = async (userId) => {
  try {
    const membersRef = collection(db, 'users', userId, 'members');
    const q = query(membersRef, orderBy('name'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user members:', error);
    throw error;
  }
};

/**
 * Add a new member to user's saved members
 * @param {string} userId - The user ID
 * @param {Object} memberData - Member data {name, email}
 * @returns {Promise<Object>} Created member object
 */
export const addUserMember = async (userId, memberData) => {
  try {
    const membersRef = collection(db, 'users', userId, 'members');
    const docRef = await addDoc(membersRef, {
      name: memberData.name,
      email: memberData.email || null,
      createdAt: Timestamp.now()
    });

    return {
      id: docRef.id,
      name: memberData.name,
      email: memberData.email || null,
      createdAt: Timestamp.now()
    };
  } catch (error) {
    console.error('Error adding user member:', error);
    throw error;
  }
};

/**
 * Update a user's saved member
 * @param {string} userId - The user ID
 * @param {string} memberId - The member ID
 * @param {Object} memberData - Updated member data {name, email}
 * @returns {Promise<void>}
 */
export const updateUserMember = async (userId, memberId, memberData) => {
  try {
    const memberRef = doc(db, 'users', userId, 'members', memberId);
    await updateDoc(memberRef, {
      name: memberData.name,
      email: memberData.email || null
    });
  } catch (error) {
    console.error('Error updating user member:', error);
    throw error;
  }
};

/**
 * Delete a user's saved member
 * @param {string} userId - The user ID
 * @param {string} memberId - The member ID
 * @returns {Promise<void>}
 */
export const deleteUserMember = async (userId, memberId) => {
  try {
    const memberRef = doc(db, 'users', userId, 'members', memberId);
    await deleteDoc(memberRef);
  } catch (error) {
    console.error('Error deleting user member:', error);
    throw error;
  }
};
